<template>
  <div class="coa-workspace" :aria-busy="loading || bulkBusy">
    <header class="coa-heading">
      <div><p class="coa-eyebrow">DAFTAR AKUN</p><h2>Kelola COA</h2><p>{{ company.name || 'Perusahaan' }} &middot; Tahun buku {{ fiscalYear || selectedYear }}</p></div>
      <div class="coa-actions"><vs-button type="border" :disabled="busy" @click="load">Muat ulang</vs-button><vs-button type="flat" to="/accounts-number">Daftar akun</vs-button></div>
    </header>
    <div v-if="!company.id" class="coa-state">Pilih perusahaan aktif untuk mengelola akun.</div>
    <template v-else>
      <div class="coa-metrics">
        <button @click="setQuickFilter('all')"><strong>{{ postingAccounts.length }}</strong><span>Akun transaksi</span></button>
        <button @click="setQuickFilter('uncategorized')"><strong>{{ postingAccounts.filter(a => !a.account_category).length }}</strong><span>Belum berkategori</span></button>
        <button @click="setQuickFilter('review')"><strong>{{ postingAccounts.filter(a => a.needs_review).length }}</strong><span>Perlu ditinjau</span></button>
        <button @click="setQuickFilter('unmapped')"><strong>{{ postingAccounts.filter(a => !isMapped(a.mapping)).length }}</strong><span>Belum terhubung lintas tahun</span></button>
      </div>
      <div v-if="loadError" class="coa-state coa-error" role="alert">{{ loadError }} <button @click="load">Coba lagi</button></div>
      <div v-if="successMessage" class="coa-success" role="status">{{ successMessage }}</div>
      <div class="coa-layout">
        <aside class="coa-navigation" aria-label="Kelompok kode akun">
          <h4>Kelompok kode</h4><p>Kode mengikuti struktur perusahaan.</p>
          <button :class="{ active: !prefixFilter }" @click="prefixFilter = ''"><span>Semua kelompok</span><b>{{ accounts.length }}</b></button>
          <button v-for="group in codeGroups" :key="group.prefix" :class="{ active: prefixFilter === group.prefix, subgroup: group.depth === 2 }" @click="prefixFilter = group.prefix">
            <span><small>{{ group.prefix }}</small>{{ group.name }}</span><b>{{ group.count }}</b>
          </button>
          <p class="coa-nav-note">Header dan subtotal tersedia lewat filter jenis akun. Keduanya tidak dihitung sebagai akun transaksi yang belum lengkap.</p>
        </aside>
        <main class="coa-content">
          <div class="coa-tabs" role="tablist" aria-label="Pengelolaan akun">
            <button role="tab" :aria-selected="activeTab === 'category'" :class="{ active: activeTab === 'category' }" @click="activeTab = 'category'">Kategori &amp; saldo normal</button>
            <button role="tab" :aria-selected="activeTab === 'identity'" :class="{ active: activeTab === 'identity' }" @click="activeTab = 'identity'">Identitas lintas tahun</button>
          </div>
          <div class="coa-filters">
            <div class="coa-search"><label for="coa-search">Cari akun</label><input id="coa-search" v-model="search" type="search" placeholder="Kode atau nama akun..." /></div>
            <div><label for="coa-kind">Jenis akun</label><select id="coa-kind" v-model="kindFilter"><option value="POSTING">Akun transaksi</option><option value="HEADER">Header / kelompok</option><option value="DERIVED">Subtotal laporan</option><option value="all">Semua jenis</option></select></div>
            <div><label for="coa-category">Kategori</label><select id="coa-category" v-model="categoryFilter"><option value="all">Semua kategori</option><option v-for="option in categoryOptions" :key="option.value" :value="option.value">{{ option.label }}</option></select></div>
            <div><label for="coa-status">Status</label><select id="coa-status" v-model="statusFilter"><option value="all">Semua status</option><option value="uncategorized">Belum berkategori</option><option value="review">Perlu ditinjau</option><option value="unmapped">Belum terhubung</option></select></div>
          </div>
          <div class="coa-results"><span>{{ visibleAccounts.length }} akun sesuai filter</span><button @click="resetFilters">Reset filter</button><button v-if="activeTab === 'category'" :disabled="busy" @click="loadPreview">Lihat saran otomatis</button></div>
          <div v-if="selectedIds.length" class="coa-selection" role="status">
            <div><strong>{{ selectedIds.length }} akun dipilih</strong><small v-if="hiddenSelectionCount">{{ hiddenSelectionCount }} pilihan berada di luar filter saat ini.</small></div>
            <button :disabled="busy" @click="openBulk">Ubah kategori terpilih</button><button :disabled="bulkBusy" @click="clearSelection">Batalkan pilihan</button>
          </div>
          <section v-if="bulkOpen" class="coa-bulk" aria-labelledby="coa-bulk-title">
            <div class="coa-section-title"><h3 id="coa-bulk-title">Ubah {{ bulkIds.length }} akun sekaligus</h3><button :disabled="bulkBusy" @click="closeBulk">Tutup</button></div>
            <p>Pilih kategori, periksa ringkasan, lalu simpan untuk tahun {{ bulkContext.year }}.</p>
            <div class="coa-bulk-fields">
              <div><label for="bulk-category">Kategori tujuan</label><select id="bulk-category" ref="bulkCategory" v-model="bulkCategory" :disabled="bulkBusy" @change="invalidateBulkPreview"><option value="">Pilih kategori</option><option v-for="option in categoryOptions" :key="option.value" :value="option.value">{{ option.label }}</option></select></div>
              <div><label for="bulk-normal">Saldo normal</label><select id="bulk-normal" v-model="bulkNormal" :disabled="bulkBusy" @change="invalidateBulkPreview"><option value="preserve">Pertahankan masing-masing akun</option><option value="debit">Ubah semua ke Debit</option><option value="credit">Ubah semua ke Kredit</option></select></div>
              <vs-button :disabled="!bulkCategory || bulkBusy" type="border" @click="previewBulk">{{ bulkBusy ? 'Memproses...' : 'Periksa perubahan' }}</vs-button>
            </div>
            <p class="coa-help">Saldo normal yang masih kosong mengikuti kategori. Kategori laporan dan aturan tutup buku disesuaikan otomatis; akun kontra tetap mengikuti saldo normalnya.</p>
            <p v-if="bulkError" class="coa-error" role="alert">{{ bulkError }}</p>
            <template v-if="bulkPreview">
              <div class="coa-preview-summary"><strong>{{ bulkPreview.changed }} dari {{ bulkPreview.selected }} akun akan diperbarui</strong><p>Periksa kembali pemetaan bagian laporan jika kategori akun berubah.</p></div>
              <div class="coa-preview-table"><table class="manage-coa-table"><thead><tr><th>Akun</th><th>Kategori sebelumnya</th><th>Kategori baru</th><th>Saldo normal</th></tr></thead><tbody><tr v-for="row in bulkPreview.rows" :key="row.id"><td><code>{{ row.account_code }}</code><span>{{ row.name }}</span></td><td>{{ categoryLabel(row.before.account_category) }}</td><td>{{ categoryLabel(row.after.account_category) }}</td><td>{{ balanceLabel(row.before.normal_balance) }} &rarr; {{ balanceLabel(row.after.normal_balance) }}</td></tr></tbody></table></div>
              <vs-button :disabled="bulkBusy || !bulkPreview.changed" @click="applyBulk">{{ bulkBusy ? 'Menyimpan...' : `Simpan ${bulkPreview.changed} perubahan` }}</vs-button>
            </template>
          </section>
          <section v-if="previewRows.length" class="coa-bulk">
            <div class="coa-section-title"><h3>Saran otomatis</h3><button @click="previewRows = []; previewToken = ''">Tutup</button></div>
            <p>{{ previewSummary.suggested }} usulan &middot; {{ previewSummary.needs_review }} perlu ditinjau. Klasifikasi manual tetap dipertahankan.</p>
            <div class="coa-preview-table"><table class="manage-coa-table"><thead><tr><th>Akun</th><th>Saat ini</th><th>Usulan</th><th>Status</th></tr></thead><tbody><tr v-for="row in visiblePreviewRows" :key="row.id"><td><code>{{ row.account_no }}</code><span>{{ row.account_name }}</span></td><td>{{ categoryLabel(row.current_category) }}</td><td>{{ categoryLabel(row.suggested_category) }}</td><td>{{ row.needs_review ? 'Perlu ditinjau' : 'Tersedia' }}</td></tr></tbody></table></div>
            <vs-button :disabled="busy" type="border" @click="applyPreview">Terapkan saran otomatis</vs-button>
          </section>
          <div v-if="loading" class="coa-state" role="status">Memuat akun dan kelompok kode...</div>
          <template v-else-if="!loadError">
            <div class="coa-results"><button :disabled="busy || !selectableFiltered.length" @click="selectFiltered">Pilih seluruh hasil ({{ selectableFiltered.length }})</button><span>Subtotal laporan tidak dapat dipilih untuk perubahan kategori.</span></div>
            <div class="coa-table-wrap"><table class="manage-coa-table">
              <thead><tr><th class="coa-checkbox"><input type="checkbox" aria-label="Pilih semua akun pada halaman ini" :checked="allPageSelected" :disabled="busy || !selectablePage.length" @change="togglePage($event.target.checked)" /></th><th>Kode &amp; nama akun</th><template v-if="activeTab === 'category'"><th>Kategori</th><th>Saldo normal</th><th>Status</th></template><template v-else><th>Identitas lintas tahun</th><th>Akun sumber</th></template><th>Aksi</th></tr></thead>
              <tbody><tr v-for="account in pagedAccounts" :key="account.id" :class="{ selected: selectedIds.includes(account.id) }">
                <td><input type="checkbox" :aria-label="`Pilih ${account.account_code} ${account.name}`" :disabled="busy || account.account_kind === 'DERIVED'" :checked="selectedIds.includes(account.id)" @change="toggleAccount(account.id, $event.target.checked)" /></td>
                <td class="coa-account"><code>{{ account.account_code }}</code><strong>{{ account.name }}</strong><small v-if="account.account_kind !== 'POSTING'">{{ account.account_kind === 'DERIVED' ? 'Subtotal laporan' : 'Header / kelompok' }}</small></td>
                <template v-if="activeTab === 'category'"><td><span class="coa-badge" :class="{ muted: !account.account_category }">{{ account.account_kind === 'DERIVED' ? 'Dihitung dari laporan' : categoryLabel(account.account_category) }}</span></td><td>{{ balanceLabel(account.normal_balance) }}</td><td><span v-if="account.account_kind === 'DERIVED'">Bukan akun transaksi</span><span v-else-if="account.needs_review" class="coa-warning">Perlu ditinjau</span><span v-else>{{ account.mapping_source === 'MANUAL' ? 'Diatur manual' : (account.account_category ? 'Sudah berkategori' : 'Belum berkategori') }}</span></td></template>
                <template v-else><td>{{ mappingLabel(account.mapping) }}<details v-if="isMapped(account.mapping)"><summary>Lihat identitas</summary><small class="coa-uid">{{ account.mapping.account_uid }}</small></details></td><td>{{ sourceLabel(account.mapping) }}</td></template>
                <td><button v-if="activeTab === 'category' && account.account_kind !== 'DERIVED'" :disabled="busy" @click="openCategory(account)">Ubah kategori</button><template v-if="activeTab === 'identity'"><button :disabled="busy" @click="openMapping(account)">{{ isMapped(account.mapping) ? 'Ubah hubungan' : 'Hubungkan akun' }}</button><button v-if="isMapped(account.mapping)" :disabled="busy" class="coa-danger" @click="openUnmap(account)">Lepas</button></template></td>
              </tr><tr v-if="!pagedAccounts.length"><td :colspan="activeTab === 'category' ? 6 : 5" class="coa-state">Tidak ada akun sesuai filter. <button @click="resetFilters">Tampilkan semua akun transaksi</button></td></tr></tbody>
            </table></div>
            <div class="coa-pagination"><span>Halaman {{ page }} dari {{ pageCount }} &middot; {{ visibleAccounts.length }} akun</span><label>Per halaman <select v-model.number="pageSize"><option :value="25">25</option><option :value="50">50</option><option :value="100">100</option></select></label><button :disabled="page <= 1" @click="page--">Sebelumnya</button><button :disabled="page >= pageCount" @click="page++">Berikutnya</button></div>
          </template>
        </main>
      </div>
    </template>
    <vs-prompt
      title="Mapping UID Akun"
      :accept-text="needsUidCorrection ? 'Preview koreksi' : 'Simpan mapping'"
      cancel-text="Batal"
      :active.sync="mappingOpen"
      :is-valid="mappingValid"
      @accept="saveMapping">
      <p class="mb-3">{{ selectedAccount ? selectedAccount.account_code + ' - ' + selectedAccount.name : '' }}</p>
      <label class="block mb-2"><input v-model="newIdentity" type="radio" :value="true" /> Identitas akun baru</label>
      <label class="block mb-3"><input v-model="newIdentity" type="radio" :value="false" /> Lanjutan akun tahun sebelumnya</label>
      <template v-if="!newIdentity">
        <vs-input v-model="referenceSearch" class="w-full mb-2" placeholder="Cari akun tahun sebelumnya" />
        <select v-model="sourceMappingId" class="w-full p-2 border rounded mb-3">
          <option value="">Pilih akun sumber</option>
          <option v-for="source in filteredReferences" :key="source.id" :value="source.id">
            {{ source.account_code_snapshot }} - {{ source.account_name_snapshot }} ({{ source.fiscal_year }})
          </option>
        </select>
      </template>
      <label>Alasan perubahan</label>
      <textarea v-model="mappingReason" class="w-full p-2 border rounded" rows="3" placeholder="Jelaskan keputusan mapping ini"></textarea>
      <p class="text-sm opacity-75 mt-2">Dikelola role 0/1 dan tercatat di riwayat. UID berbeda akan menampilkan preview tahun terdampak sebelum disimpan.</p>
    </vs-prompt>

    <vs-prompt title="Konfirmasi koreksi UID berantai" accept-text="Terapkan koreksi" cancel-text="Batal"
      :active.sync="correctionOpen" :is-valid="correctionConfirmed && !mappingBusy" @accept="applyUidCorrection">
      <template v-if="correctionPreview">
        <p class="mb-2">Seluruh akun berikut akan memakai UID sumber yang dipilih:</p>
        <p class="text-xs break-all mb-2">{{ correctionPreview.old_uid }} → {{ correctionPreview.new_uid }}</p>
        <div class="overflow-auto" style="max-height: 35vh">
          <table class="w-full manage-coa-table">
            <thead><tr><th>Tahun</th><th>Akun</th><th>Referensi laporan</th></tr></thead>
            <tbody><tr v-for="row in correctionPreview.rows" :key="row.year + '-' + row.account_id">
              <td>{{ row.year }}</td><td>{{ row.name }}</td><td>{{ row.report_references }}</td>
            </tr></tbody>
          </table>
        </div>
        <p class="my-3 text-sm">Registry, UID akun, mapping laporan, dan pilihan akun laba/rugi yang merujuk UID lama diperbarui bersama. Saldo, jurnal, dan riwayat audit sebelumnya tetap.</p>
        <label><input v-model="correctionConfirmed" type="checkbox" /> Saya memastikan akun-akun ini merupakan identitas akun yang sama.</label>
      </template>
    </vs-prompt>

    <vs-prompt
      title="Kosongkan Mapping UID"
      accept-text="Kosongkan"
      cancel-text="Batal"
      :active.sync="unmapOpen"
      :is-valid="!mappingBusy && unmapReason.trim().length >= 10"
      @accept="saveUnmap">
      <p class="mb-3">{{ selectedAccount ? selectedAccount.account_code + ' - ' + selectedAccount.name : '' }}</p>
      <label>Alasan pengosongan</label>
      <textarea v-model="unmapReason" class="w-full p-2 border rounded" rows="3"></textarea>
      <p class="text-sm opacity-75">Mapping yang sudah dipakai tahun berikutnya atau UID-nya sudah ditulis ke akun tahunan tidak dapat dikosongkan langsung.</p>
    </vs-prompt>
  </div>
</template>

<script>
import axios from '@/axios.js'

export default {
  data () {
    return {
      fiscalYear: null,
      activeTab: 'category',
      kindFilter: 'POSTING',
      categoryFilter: 'all',
      statusFilter: 'all',
      prefixFilter: '',
      selectedIds: [],
      page: 1,
      pageSize: 25,
      loadError: '',
      successMessage: '',
      loadSequence: 0,
      loadedContext: null,
      bulkOpen: false,
      bulkBusy: false,
      bulkCategory: '',
      bulkNormal: 'preserve',
      bulkIds: [],
      bulkContext: null,
      bulkPreview: null,
      bulkRequest: null,
      bulkError: '',

      accounts: [],
      referenceMappings: [],
      loading: false,
      previewLoading: false,
      applying: false,
      previewRows: [],
      autoContext: null,
      previewToken: '',
      previewSummary: {},
      search: '',
      selectedAccount: null,
      categoryOpen: false,
      categoryForm: { account_category: '', normal_balance: '', statement_type: '', closing_behavior: '' },
      categoryOptions: [
        { value: 'asset', label: 'Aset' },
        { value: 'liability', label: 'Liabilitas' },
        { value: 'equity', label: 'Ekuitas' },
        { value: 'income', label: 'Pendapatan' },
        { value: 'expense', label: 'Beban' },
        { value: 'cost_of_sales', label: 'Harga Pokok Penjualan' },
        { value: 'other_income', label: 'Pendapatan Lain-lain' },
        { value: 'other_expense', label: 'Beban Lain-lain' }
      ],
      mappingOpen: false,
      mappingBusy: false,
      mappingContext: null,
      correctionOpen: false,
      correctionConfirmed: false,
      correctionPreview: null,
      correctionRequest: null,
      newIdentity: true,
      sourceMappingId: '',
      referenceSearch: '',
      mappingReason: '',
      unmapOpen: false,
      unmapReason: ''
    }
  },
  computed: {
    company () {
      return JSON.parse(localStorage.getItem('activeCompany') || 'null') || {}
    },
    selectedYear () {
      return localStorage.getItem('selectedYear')
    },
    busy () { return this.loading || this.bulkBusy || this.mappingBusy || this.applying || this.previewLoading },
    postingAccounts () { return this.accounts.filter(account => account.account_kind === 'POSTING') },
    codeGroups () {
      const groups = []
      const roots = [...new Set(this.accounts.map(account => account.account_code.split('.')[0]))].sort()
      roots.forEach(prefix => {
        const rows = this.accounts.filter(account => account.account_code.startsWith(`${prefix}.`))
        const root = rows.find(account => account.account_code === `${prefix}.00.00.000`)
        groups.push({ prefix, name: root ? root.name : 'Kelompok akun', count: rows.length, depth: 1 })
        const children = [...new Set(rows.map(account => account.account_code.split('.').slice(0, 2).join('.')))].filter(key => !key.endsWith('.00')).sort()
        children.forEach(childPrefix => {
          const childRows = rows.filter(item => item.account_code.startsWith(`${childPrefix}.`))
          const header = childRows.find(account => account.account_code === `${childPrefix}.00.000`)
          const categories = [...new Set(childRows.filter(account => account.account_kind === 'POSTING' && account.account_category).map(account => account.account_category))]
          const name = header ? header.name : (categories.length === 1 ? this.categoryLabel(categories[0]) : 'Kelompok campuran')
          groups.push({ prefix: childPrefix, name, count: childRows.length, depth: 2 })
        })
      })
      return groups
    },
    visibleAccounts () {
      const query = this.search.trim().toLowerCase()
      return this.accounts.filter(account => {
        if (this.kindFilter !== 'all' && account.account_kind !== this.kindFilter) return false
        if (this.prefixFilter && !account.account_code.startsWith(`${this.prefixFilter}.`)) return false
        if (this.categoryFilter !== 'all' && account.account_category !== this.categoryFilter) return false
        if (this.statusFilter === 'uncategorized' && (account.account_category || account.account_kind !== 'POSTING')) return false
        if (this.statusFilter === 'review' && !account.needs_review) return false
        if (this.statusFilter === 'unmapped' && this.isMapped(account.mapping)) return false
        return !query || `${account.account_code} ${account.name}`.toLowerCase().includes(query)
      })
    },
    pageCount () { return Math.max(1, Math.ceil(this.visibleAccounts.length / this.pageSize)) },
    pagedAccounts () { return this.visibleAccounts.slice((this.page - 1) * this.pageSize, this.page * this.pageSize) },
    selectableFiltered () { return this.visibleAccounts.filter(account => account.account_kind !== 'DERIVED') },
    selectablePage () { return this.pagedAccounts.filter(account => account.account_kind !== 'DERIVED') },
    allPageSelected () { return !!this.selectablePage.length && this.selectablePage.every(account => this.selectedIds.includes(account.id)) },
    hiddenSelectionCount () { const visible = new Set(this.visibleAccounts.map(account => account.id)); return this.selectedIds.filter(id => !visible.has(id)).length },
    visiblePreviewRows () {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.previewRows
      return this.previewRows.filter(item => `${item.account_no} ${item.account_name}`.toLowerCase().includes(query))
    },
    mappedCount () {
      return this.accounts.filter(item => this.isMapped(item.mapping)).length
    },
    categorizedCount () {
      return this.accounts.filter(item => !!item.account_category).length
    },
    categoryValid () {
      return !!(this.categoryForm.account_category && this.categoryForm.normal_balance)
    },
    mappingValid () {
      return !this.mappingBusy && this.mappingReason.trim().length >= 10 && (this.newIdentity || !!this.sourceMappingId)
    },
    needsUidCorrection () {
      const source = this.referenceMappings.find(item => Number(item.id) === Number(this.sourceMappingId))
      const uid = this.selectedAccount && this.selectedAccount.annual_account_uid
      return !!(!this.newIdentity && source && uid && source.account_uid !== uid)
    },
    filteredReferences () {
      const query = this.referenceSearch.trim().toLowerCase()
      return this.referenceMappings
        .filter(item => !query || `${item.account_code_snapshot} ${item.account_name_snapshot} ${item.fiscal_year}`.toLowerCase().includes(query))
        .slice(0, 100)
    }
  },
  watch: {
    search () { this.page = 1 },
    kindFilter () { this.page = 1 },
    categoryFilter () { this.page = 1 },
    statusFilter () { this.page = 1 },
    prefixFilter () { this.page = 1 },
    pageSize () { this.page = 1 }
  },
  beforeDestroy () { this.loadSequence++ },
  created () {
    if (this.company.id) this.load()
  },
  methods: {
    async load () {
      if (this.bulkBusy) return
      const context = { companyId: this.company.id, year: String(this.selectedYear) }
      if (!context.companyId) return
      const sequence = ++this.loadSequence
      this.loading = true
      this.loadError = ''
      this.loadedContext = null
      this.mappingContext = null
      this.mappingOpen = false
      this.unmapOpen = false
      this.correctionOpen = false
      this.correctionRequest = null
      this.correctionPreview = null
      this.accounts = []
      this.selectedIds = []
      this.page = 1
      this.closeBulk()
      this.previewRows = []
      this.previewToken = ''
      try {
        const response = await axios.get(`account-uid-mappings/${context.companyId}`, { headers: { 'X-Year': context.year } })
        if (sequence !== this.loadSequence) return
        if (!this.correctionContextValid(context)) throw new Error('Perusahaan atau tahun berubah. Muat ulang daftar akun.')
        const data = response.data.data
        this.fiscalYear = data.fiscal_year
        this.loadedContext = context
        this.accounts = (data.accounts || []).map(account => ({ ...account, id: Number(account.id), account_kind: account.account_kind || (account.is_header ? 'HEADER' : 'POSTING') }))
        this.referenceMappings = data.reference_mappings || []
      } catch (error) {
        if (sequence !== this.loadSequence) return
        this.loadError = (error.response && error.response.data && error.response.data.message) || error.message || 'Daftar akun gagal dimuat.'
      } finally { if (sequence === this.loadSequence) this.loading = false }
    },
    resetFilters () { this.search = ''; this.kindFilter = 'POSTING'; this.categoryFilter = 'all'; this.statusFilter = 'all'; this.prefixFilter = ''; this.page = 1 },
    setQuickFilter (status) { this.resetFilters(); this.statusFilter = status },
    balanceLabel (value) { return value === 'debit' ? 'Debit' : (value === 'credit' ? 'Kredit' : '-') },
    toggleAccount (id, checked) {
      if (this.busy) return
      const account = this.accounts.find(item => item.id === id)
      if (!account || account.account_kind === 'DERIVED') return
      this.selectedIds = checked ? [...new Set([...this.selectedIds, id])] : this.selectedIds.filter(item => item !== id)
      this.closeBulk()
    },
    togglePage (checked) {
      if (this.busy) return
      const ids = this.selectablePage.map(account => account.id)
      this.selectedIds = checked ? [...new Set([...this.selectedIds, ...ids])] : this.selectedIds.filter(id => !ids.includes(id))
      this.closeBulk()
    },
    selectFiltered () {
      if (this.busy) return
      this.selectedIds = [...new Set([...this.selectedIds, ...this.selectableFiltered.map(account => account.id)])]
      this.closeBulk()
    },
    clearSelection () { if (this.bulkBusy) return; this.selectedIds = []; this.closeBulk() },
    closeBulk () { if (this.bulkBusy) return; this.bulkOpen = false; this.bulkPreview = null; this.bulkRequest = null; this.bulkError = '' },
    openBulk () {
      if (this.busy || !this.selectedIds.length || !this.loadedContext) return
      if (!this.correctionContextValid(this.loadedContext)) { this.notifyError({ response: { data: { message: 'Perusahaan atau tahun berubah. Muat ulang daftar.' } } }); return }
      this.bulkIds = [...this.selectedIds]
      this.bulkContext = { ...this.loadedContext }
      this.bulkCategory = ''
      this.bulkNormal = 'preserve'
      this.invalidateBulkPreview()
      this.bulkOpen = true
      this.$nextTick(() => { if (this.$refs.bulkCategory) this.$refs.bulkCategory.focus() })
    },
    invalidateBulkPreview () { this.bulkPreview = null; this.bulkRequest = null; this.bulkError = '' },
    async previewBulk () {
      if (this.bulkBusy || !this.bulkCategory || !this.bulkContext) return
      if (!this.correctionContextValid(this.bulkContext)) { this.bulkError = 'Perusahaan atau tahun berubah. Muat ulang daftar.'; return }
      this.bulkBusy = true
      this.invalidateBulkPreview()
      const payload = { account_ids: [...this.bulkIds], fiscal_year: Number(this.bulkContext.year), account_category: this.bulkCategory, normal_balance: this.bulkNormal }
      try {
        const response = await axios.post(`coa-classification/${this.bulkContext.companyId}/bulk-preview`, payload, { headers: { 'X-Year': this.bulkContext.year } })
        if (!this.correctionContextValid(this.bulkContext)) { this.bulkError = 'Perusahaan atau tahun berubah. Muat ulang daftar.'; return }
        this.bulkPreview = response.data.data
        this.bulkRequest = payload
      } catch (error) { this.bulkError = (error.response && error.response.data && error.response.data.message) || 'Ringkasan perubahan gagal dimuat.' } finally { this.bulkBusy = false }
    },
    async applyBulk () {
      if (this.bulkBusy || !this.bulkPreview || !this.bulkRequest || !this.bulkPreview.changed) return
      if (!this.correctionContextValid(this.bulkContext)) { this.invalidateBulkPreview(); this.bulkError = 'Perusahaan atau tahun berubah. Muat ulang daftar.'; return }
      if (this.bulkCategory !== this.bulkRequest.account_category || this.bulkNormal !== this.bulkRequest.normal_balance) { this.invalidateBulkPreview(); return }
      this.bulkBusy = true
      this.bulkError = ''
      let saved = false
      try {
        const response = await axios.post(`coa-classification/${this.bulkContext.companyId}/bulk-apply`, { ...this.bulkRequest, preview_token: this.bulkPreview.preview_token }, { headers: { 'X-Year': this.bulkContext.year } })
        this.successMessage = `${response.data.data.updated} akun berhasil diperbarui untuk tahun ${this.bulkContext.year}.`
        this.bulkPreview = null
        this.bulkRequest = null
        saved = true
      } catch (error) {
        this.bulkPreview = null
        this.bulkRequest = null
        this.bulkError = (error.response && error.response.data && error.response.data.message) || 'Perubahan belum dapat dikonfirmasi. Muat ulang daftar sebelum mencoba lagi.'
      } finally { this.bulkBusy = false }
      if (saved) await this.load()
    },
    async loadPreview () {
      if (this.busy || !this.loadedContext) return
      const context = { ...this.loadedContext }
      if (!this.correctionContextValid(context)) { this.notifyError({ message: 'Perusahaan atau tahun berubah. Muat ulang daftar.' }); return }
      this.previewLoading = true
      this.previewRows = []
      this.previewToken = ''
      try {
        const response = await axios.get(`coa-classification/${context.companyId}/preview`, { headers: { 'X-Year': context.year } })
        if (!this.correctionContextValid(context)) return
        const data = response.data.data
        this.autoContext = context
        this.previewRows = data.rows || []
        this.previewToken = data.preview_token
        this.previewSummary = data.summary || {}
      } catch (error) { this.notifyError(error) } finally { this.previewLoading = false }
    },
    async applyPreview () {
      if (this.busy || !this.previewToken || !this.autoContext) return
      if (!this.correctionContextValid(this.autoContext)) { this.notifyError({ message: 'Perusahaan atau tahun berubah. Buat ulang saran otomatis.' }); return }
      if (!window.confirm('Terapkan usulan klasifikasi COA tahun aktif? Akun manual tetap dipertahankan.')) return
      this.applying = true
      let saved = false
      try {
        const response = await axios.post(`coa-classification/${this.autoContext.companyId}/apply`, { preview_token: this.previewToken }, { headers: { 'X-Year': this.autoContext.year } })
        this.successMessage = `${response.data.data.updated} akun diperbarui dari saran otomatis.`
        this.previewToken = ''
        saved = true
      } catch (error) { this.previewToken = ''; this.notifyError(error) } finally { this.applying = false }
      if (saved) await this.load()
    },
    categoryLabel (value) {
      const option = this.categoryOptions.find(item => item.value === value)
      return option ? option.label : 'Belum diklasifikasikan'
    },
    isMapped (mapping) {
      return !!(mapping && mapping.status === 'APPROVED' && mapping.account_uid)
    },
    mappingLabel (mapping) {
      return this.isMapped(mapping) ? 'Terpetakan' : 'Belum terpetakan'
    },
    sourceLabel (mapping) {
      if (!this.isMapped(mapping)) return '-'
      if (!mapping.source_mapping_id) return 'Identitas baru'
      const source = this.referenceMappings.find(item => Number(item.id) === Number(mapping.source_mapping_id))
      return source ? `${source.account_code_snapshot} (${source.fiscal_year})` : `Mapping #${mapping.source_mapping_id}`
    },
    openCategory (account) {
      if (account.account_kind === 'DERIVED' || this.busy) return
      this.selectedIds = [account.id]
      this.openBulk()
      this.bulkCategory = account.account_category || ''
    },
    onCategoryChange () {
      const category = this.categoryForm.account_category
      const permanent = ['asset', 'liability', 'equity'].includes(category)
      this.categoryForm.statement_type = category ? (permanent ? 'balance_sheet' : 'income_statement') : ''
      this.categoryForm.closing_behavior = category ? (permanent ? 'carry_forward' : 'close_to_equity') : ''
      const debitCategories = ['asset', 'expense', 'cost_of_sales', 'other_expense']
      this.categoryForm.normal_balance = category ? (debitCategories.includes(category) ? 'debit' : 'credit') : ''
    },
    saveCategory () {
      if (!this.selectedAccount || !this.categoryValid) return
      axios.patch(`coa-classification/accounts/${this.selectedAccount.id}/manual`, {
        account_category: this.categoryForm.account_category,
        normal_balance: this.categoryForm.normal_balance
      })
        .then(() => {
          this.$vs.notify({ color: 'success', title: 'Berhasil', text: 'Kategori akun tersimpan.' })
          this.load()
        }).catch(error => this.notifyError(error))
    },
    openMapping (account) {
      if (!this.openUidAction(account, 'mapping')) return
      this.selectedAccount = account
      const mapping = account.mapping
      this.newIdentity = !(mapping && mapping.source_mapping_id)
      this.sourceMappingId = mapping && mapping.source_mapping_id ? mapping.source_mapping_id : ''
      this.referenceSearch = ''
      this.mappingReason = ''
      this.mappingOpen = true
    },
    saveMapping () {
      if (!this.selectedAccount || !this.mappingValid) return
      const context = this.validUidAction('mapping')
      if (!context) return
      if (this.needsUidCorrection) {
        this.previewUidCorrection()
        return
      }
      this.mappingBusy = true
      return axios.put(`account-uid-mappings/${context.companyId}/${context.accountId}`, {
        new_identity: this.newIdentity,
        source_mapping_id: this.newIdentity ? null : this.sourceMappingId,
        reason: this.mappingReason.trim()
      }, { headers: { 'X-Year': context.year } }).then(() => {
        this.mappingContext = null
        this.$vs.notify({ color: 'success', title: 'Berhasil', text: 'Mapping UID tersimpan.' })
        this.load()
      }).catch(error => this.notifyError(error)).finally(() => { this.mappingBusy = false })
    },
    openUnmap (account) {
      if (!this.openUidAction(account, 'unmap')) return
      this.selectedAccount = account
      this.unmapReason = ''
      this.unmapOpen = true
    },
    openUidAction (account, action) {
      if (this.busy) return false
      if (!this.loadedContext || !this.correctionContextValid(this.loadedContext)) {
        this.$vs.notify({ color: 'warning', title: 'Data kedaluwarsa', text: 'Perusahaan atau tahun berubah. Muat ulang daftar akun.' })
        return false
      }
      this.mappingContext = { ...this.loadedContext, accountId: account.id, action }
      this.correctionRequest = null
      this.correctionPreview = null
      return true
    },
    validUidAction (action) {
      const context = this.mappingContext
      if (this.mappingBusy || !context || context.action !== action || !this.selectedAccount || context.accountId !== this.selectedAccount.id) return null
      if (!this.correctionContextValid(context)) {
        this.$vs.notify({ color: 'warning', title: 'Data kedaluwarsa', text: 'Perusahaan atau tahun berubah. Muat ulang daftar akun.' })
        return null
      }
      return context
    },
    previewUidCorrection () {
      const actionContext = this.validUidAction('mapping')
      if (!actionContext || !this.mappingValid) return
      const context = {
        ...actionContext,
        payload: { source_mapping_id: this.sourceMappingId, reason: this.mappingReason.trim() }
      }
      this.mappingBusy = true
      this.correctionPreview = null
      this.correctionRequest = null
      this.correctionConfirmed = false
      return axios.post(`account-uid-mappings/${context.companyId}/${context.accountId}/correction-preview`, context.payload, { headers: { 'X-Year': context.year } })
        .then(response => {
          if (!this.correctionContextValid(context) || this.mappingContext !== actionContext) return
          this.correctionRequest = context
          this.correctionPreview = response.data.data
          this.mappingOpen = false
          this.correctionOpen = true
        }).catch(error => this.notifyError(error))
        .finally(() => { this.mappingBusy = false })
    },
    correctionContextValid (context) {
      const company = JSON.parse(localStorage.getItem('activeCompany') || 'null') || {}
      return String(company.id) === String(context.companyId) && String(localStorage.getItem('selectedYear')) === context.year
    },
    applyUidCorrection () {
      const context = this.correctionRequest
      if (!context || !this.correctionPreview || !this.correctionConfirmed || this.mappingBusy) return
      if (!this.correctionContextValid(context)) {
        this.$vs.notify({ color: 'warning', title: 'Preview kedaluwarsa', text: 'Company/tahun berubah. Muat ulang lalu buat preview kembali.' })
        return
      }
      this.mappingBusy = true
      return axios.post(`account-uid-mappings/${context.companyId}/${context.accountId}/correct`, {
        ...context.payload, preview_token: this.correctionPreview.preview_token, confirmed: true
      }, { headers: { 'X-Year': context.year } }).then(response => {
        this.$vs.notify({ color: 'success', title: 'UID dikoreksi', text: `${response.data.data.rows.length} tahun diperbarui. Saldo dan jurnal tidak diubah. Buka General Ledger untuk sync saldo awal.` })
        this.correctionPreview = null
        this.correctionRequest = null
        this.load()
      }).catch(error => {
        this.correctionPreview = null
        this.correctionRequest = null
        this.notifyError(error)
      }).finally(() => { this.mappingBusy = false })
    },
    saveUnmap () {
      if (!this.selectedAccount || this.unmapReason.trim().length < 10) return
      const context = this.validUidAction('unmap')
      if (!context) return
      this.mappingBusy = true
      return axios.delete(`account-uid-mappings/${context.companyId}/${context.accountId}`, {
        headers: { 'X-Year': context.year },
        data: { reason: this.unmapReason.trim() }
      }).then(() => {
        this.mappingContext = null
        this.$vs.notify({ color: 'success', title: 'Berhasil', text: 'Mapping UID dikosongkan.' })
        this.load()
      }).catch(error => this.notifyError(error)).finally(() => { this.mappingBusy = false })
    },
    notifyError (error) {
      const response = error && error.response && error.response.data
      const errors = response && response.errors
      const firstError = errors && Object.keys(errors).length ? errors[Object.keys(errors)[0]][0] : null
      this.$vs.notify({ color: 'danger', title: 'Gagal', text: firstError || (response && response.message) || (error && error.message) || 'Data COA tidak dapat diproses.' })
    }
  }
}
</script>

<style scoped>
.coa-workspace { color: #25334a; max-width: 1680px; margin: 0 auto; }
.coa-heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin: 8px 0 24px; }
.coa-heading h2 { margin: 6px 0; font-size: 26px; font-weight: 600; }
.coa-heading p { color: #627086; margin: 0; }
.coa-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; }
.coa-actions { display: flex; gap: 8px; }
.coa-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 24px; }
.coa-metrics button { display: flex; flex-direction: column; gap: 6px; text-align: left; background: #fff; border: 1px solid #dde4ed; border-radius: 10px; padding: 18px 20px; color: #40516c; cursor: pointer; }
.coa-metrics strong { font-size: 25px; color: #21314c; font-variant-numeric: tabular-nums; }
.coa-metrics span { font-size: 12px; }
.coa-layout { display: grid; grid-template-columns: 235px minmax(0, 1fr); gap: 20px; align-items: start; }
.coa-navigation, .coa-content { background: #fff; border: 1px solid #e0e6ee; border-radius: 10px; }
.coa-navigation { padding: 18px 10px; max-height: 80vh; overflow-y: auto; position: sticky; top: 90px; }
.coa-navigation h4, .coa-navigation p { padding: 0 10px; }
.coa-navigation p { color: #6a778a; font-size: 12px; line-height: 1.6; margin: 10px 0 16px; }
.coa-navigation button { display: flex; align-items: center; justify-content: space-between; gap: 10px; text-align: left; width: 100%; border: 0; background: transparent; color: #45546b; padding: 10px; border-radius: 6px; font-size: 12px; cursor: pointer; }
.coa-navigation button:hover { background: var(--app-primary-soft); }
.coa-navigation button.active { background: var(--app-primary-soft); color: var(--app-primary-ink); font-weight: 600; }
.coa-navigation small { display: block; color: #78859a; font-size: 11px; margin-bottom: 4px; }
.coa-navigation b { color: #6b7990; font-weight: 400; }
.coa-navigation .subgroup { padding-left: 24px; }
.coa-nav-note { border-top: 1px solid #e0e6ee; padding-top: 14px !important; }
.coa-content { min-width: 0; padding: 0 20px 16px; }
.coa-tabs { display: flex; gap: 24px; border-bottom: 1px solid #e0e6ee; margin-bottom: 20px; overflow-x: auto; }
.coa-tabs button { border: 0; border-bottom: 3px solid transparent; background: transparent; color: #68758a; padding: 18px 0 14px; white-space: nowrap; cursor: pointer; }
.coa-tabs button.active { color: var(--app-primary-ink); border-color: var(--app-primary-ink); font-weight: 600; }
.coa-filters { display: flex; flex-wrap: wrap; gap: 12px; }
.coa-filters > div { flex: 1 1 145px; min-width: 0; }
.coa-filters .coa-search { flex: 2 1 220px; }
.coa-filters label, .coa-bulk-fields label { display: block; font-size: 12px; font-weight: 600; margin-bottom: 7px; }
.coa-workspace select, .coa-workspace input[type="search"] { width: 100%; border: 1px solid #bac5d5; background: #fff; border-radius: 6px; padding: 9px 10px; color: #31415a; min-height: 39px; }
.coa-workspace input[type="checkbox"] { width: 16px; height: 16px; accent-color: var(--app-primary-ink); cursor: pointer; }
.coa-workspace button:disabled { opacity: .5; cursor: not-allowed; }
.coa-workspace button:focus-visible, .coa-workspace input:focus-visible, .coa-workspace select:focus-visible { outline: 3px solid var(--app-primary); outline-offset: 2px; }
.coa-results { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; color: #6b788e; font-size: 12px; padding: 14px 0; }
.coa-results button, .manage-coa-table button, .coa-pagination button, .coa-section-title button { background: transparent; border: 0; color: var(--app-primary-ink); cursor: pointer; font-size: 12px; padding: 5px; }
.coa-selection { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; background: var(--app-primary-soft); border: 1px solid var(--app-primary-border); padding: 12px 16px; border-radius: 8px; margin: 8px 0 16px; }
.coa-selection > div { flex: 1; }
.coa-selection small { display: block; margin-top: 5px; }
.coa-selection button { background: #fff; border: 1px solid #b6c7e8; color: var(--app-primary-ink); padding: 9px 12px; border-radius: 6px; cursor: pointer; }
.coa-bulk { background: var(--app-primary-soft); border: 1px solid #bacced; border-radius: 8px; padding: 20px; margin: 14px 0; }
.coa-section-title { display: flex; justify-content: space-between; gap: 12px; }
.coa-section-title h3 { font-size: 17px; }
.coa-bulk p { margin: 10px 0 16px; line-height: 1.6; font-size: 13px; }
.coa-bulk-fields { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 12px; }
.coa-bulk-fields > div { flex: 1 1 190px; }
.coa-help { color: #69798f; font-size: 12px !important; }
.coa-preview-table { max-height: 320px; overflow: auto; margin: 12px 0 16px; background: #fff; }
.coa-table-wrap { overflow: auto; }
.manage-coa-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.manage-coa-table th { color: #52647e; font-weight: 600; font-size: 11px; background: #f6f8fb; position: sticky; top: 0; z-index: 1; white-space: nowrap; }
.manage-coa-table th, .manage-coa-table td { padding: 13px 12px; border-bottom: 1px solid #e8edf4; text-align: left; vertical-align: middle; }
.manage-coa-table tr.selected { background: var(--app-primary-soft); }
.manage-coa-table code { display: block; color: #65748c; font-size: 11px; margin-bottom: 5px; }
.coa-account { min-width: 210px; max-width: 320px; }
.coa-account strong { font-weight: 500; }
.coa-account small { display: block; color: #7a8798; margin-top: 5px; }
.coa-badge { display: inline-block; background: var(--app-primary-soft); color: var(--app-primary-ink); border-radius: 5px; padding: 5px 8px; white-space: nowrap; }
.coa-badge.muted { background: #f1f3f6; color: #738096; }
.coa-warning { color: var(--app-secondary-ink); }
.coa-uid { display: block; max-width: 200px; overflow-wrap: anywhere; margin-top: 8px; }
.coa-danger { color: var(--app-secondary) !important; }
.coa-pagination { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 12px; padding-top: 18px; color: #64748b; font-size: 12px; }
.coa-pagination > span { margin-right: auto; }
.coa-pagination select { width: auto; min-height: 32px; padding: 5px; }
.coa-state { padding: 30px; text-align: center; }
.coa-error { color: var(--app-secondary-ink); }
.coa-success { background: #e9f5ef; color: var(--app-primary-ink); border-radius: 8px; padding: 14px 18px; margin-bottom: 16px; }
@media(max-width: 1100px) { .coa-layout { grid-template-columns: 190px minmax(0, 1fr); } .coa-content { padding: 0 14px 14px; } }
@media(max-width: 800px) { .coa-layout { grid-template-columns: 1fr; } .coa-navigation { position: static; max-height: 220px; } .coa-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); } .coa-heading { align-items: flex-start; flex-direction: column; } }
</style>
